// components/user/Section.js
import React from "react";
import { useNode } from "@craftjs/core";
import {
    Box,
    FormControl,
    FormLabel,
    TextField,
    Slider,
    Select,
    MenuItem,
    InputLabel,
    Grid,
    Typography,
    Divider,
    Switch,
    FormControlLabel,
} from "@material-ui/core";
import { ColorPicker } from './ColorPicker'; // You'll need to implement this

export const Section = ({
                            backgroundColor,
                            padding,
                            margin,
                            width,
                            height,
                            minHeight,
                            borderRadius,
                            shadow,
                            alignment,
                            children,
                        }) => {
    const {
        connectors: { connect, drag },
        selected,
        actions: { setProp }
    } = useNode((node) => ({
        selected: node.events.selected,
    }));

    const shadowPresets = {
        none: "none",
        small: "0 2px 4px rgba(0,0,0,0.1)",
        medium: "0 4px 8px rgba(0,0,0,0.1)",
        large: "0 8px 16px rgba(0,0,0,0.1)",
        custom: "0 12px 24px rgba(0,0,0,0.15)"
    };

    return (
        <div
            ref={ref => connect(drag(ref))}
            style={{ margin: `${margin}px` }}
        >
            <Box
                sx={{
                    backgroundColor,
                    padding: `${padding}px`,
                    width: width === 'custom' ? `${customWidth}px` : width,
                    height: height === 'auto' ? 'auto' : `${height}px`,
                    minHeight: `${minHeight}px`,
                    borderRadius: `${borderRadius}px`,
                    boxShadow: shadowPresets[shadow],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: alignment,
                    position: 'relative',
                }}
            >
                {children}
            </Box>

            {selected && (
                <Box
                    sx={{
                        padding: 2,
                        marginTop: 2,
                        background: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                >
                    <Typography variant="subtitle2" gutterBottom>
                        Section Properties
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel>Background Color</FormLabel>
                                <ColorPicker
                                    color={backgroundColor}
                                    onChange={color => setProp(props => props.backgroundColor = color)}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <FormLabel>Padding</FormLabel>
                                <Slider
                                    value={padding}
                                    min={0}
                                    max={100}
                                    step={4}
                                    onChange={(_, value) => setProp(props => props.padding = value)}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <FormLabel>Margin</FormLabel>
                                <Slider
                                    value={margin}
                                    min={0}
                                    max={100}
                                    step={4}
                                    onChange={(_, value) => setProp(props => props.margin = value)}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Width</InputLabel>
                                <Select
                                    value={width}
                                    onChange={e => setProp(props => props.width = e.target.value)}
                                >
                                    <MenuItem value="100%">Full Width</MenuItem>
                                    <MenuItem value="75%">75%</MenuItem>
                                    <MenuItem value="50%">50%</MenuItem>
                                    <MenuItem value="25%">25%</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Height</InputLabel>
                                <Select
                                    value={height}
                                    onChange={e => setProp(props => props.height = e.target.value)}
                                >
                                    <MenuItem value="auto">Auto</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {height === 'custom' && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <FormLabel>Custom Height</FormLabel>
                                    <TextField
                                        type="number"
                                        value={height}
                                        onChange={e => setProp(props => props.height = parseInt(e.target.value))}
                                        inputProps={{ min: 0 }}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <FormLabel>Min Height</FormLabel>
                                <TextField
                                    type="number"
                                    value={minHeight}
                                    onChange={e => setProp(props => props.minHeight = parseInt(e.target.value))}
                                    inputProps={{ min: 0 }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <FormLabel>Border Radius</FormLabel>
                                <Slider
                                    value={borderRadius}
                                    min={0}
                                    max={50}
                                    onChange={(_, value) => setProp(props => props.borderRadius = value)}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Shadow</InputLabel>
                                <Select
                                    value={shadow}
                                    onChange={e => setProp(props => props.shadow = e.target.value)}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="small">Small</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="large">Large</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Content Alignment</InputLabel>
                                <Select
                                    value={alignment}
                                    onChange={e => setProp(props => props.alignment = e.target.value)}
                                >
                                    <MenuItem value="flex-start">Start</MenuItem>
                                    <MenuItem value="center">Center</MenuItem>
                                    <MenuItem value="flex-end">End</MenuItem>
                                    <MenuItem value="stretch">Stretch</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </div>
    );
};

Section.craft = {
    props: {
        backgroundColor: "#ffffff",
        padding: 20,
        margin: 0,
        width: "100%",
        height: "auto",
        minHeight: 100,
        borderRadius: 0,
        shadow: "none",
        alignment: "flex-start",
    }
}